import {makeAutoObservable} from 'mobx';

import {
  Home,
  Me,
  Subscription,
  User,
  UserDetailed,
  userAvatarsLength,
} from '@/domain';
import {Balance} from '@/domain/models/Balance';

import {Onboardings, WebUserDetailed} from './types';

type SupportedMe = Pick<Me, 'subscription' | 'balance'>;
type SupportedHome = Pick<
  Home,
  | 'reactions'
  | 'onboardings'
  | 'pub_urls_query'
  | 'constants'
  | 'users'
  | 'unreads'
  | 'visits'
> & {
  me: SupportedMe;
};

class UserStore {
  userData: WebUserDetailed | null = null;
  // DO NOT USE home.me.user on the project, use userData instead
  home: SupportedHome | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get plusSubscription() {
    if (!this.home?.me) {
      return false;
    }

    return this.home.me.subscription?.type === 'plus';
  }

  get canUseSmash() {
    if (!this.home) {
      return false;
    }

    return this.plusSubscription || this.home?.me?.balance?.smashes > 0;
  }

  get hasSuperSmashes() {
    if (!this.home) {
      return false;
    }

    return this.home.me.balance.super_smashes > 0;
  }

  get hasSmashes() {
    if (!this.home) {
      return false;
    }

    return this.home.me.balance.smashes > 0;
  }

  get hasBoosts() {
    if (!this.home) {
      return false;
    }

    return this.home.me.balance.boosts > 0;
  }

  utilizeBoost() {
    if (!this.home) {
      return;
    }

    if (this.home.me.balance.boosts <= 0) {
      return;
    }

    this.home.me.balance.boosts -= 1;
  }

  addBoost() {
    if (!this.home) {
      return;
    }

    this.home.me.balance.boosts += 1;
  }

  get loaded() {
    return Boolean(this.home);
  }

  get enabledDiscoveryPassAndSmashSwipes() {
    return !this.home?.constants?.no_swipes;
  }

  utilizeSuperSmash() {
    if (!this.home) {
      return;
    }

    if (this.home.me.balance.super_smashes <= 0) {
      return;
    }

    this.home.me.balance.super_smashes -= 1;
  }

  utilizeSmash() {
    if (!this.home) {
      return;
    }

    if (this.plusSubscription) {
      return;
    }

    if (this.home.me.balance.smashes <= 0) {
      return;
    }

    this.home.me.balance.smashes -= 1;
  }

  setZeroSuperSmashes() {
    if (!this.home) {
      return;
    }

    this.home.me.balance.super_smashes = 0;
  }

  setUserData(userData: UserDetailed) {
    this.userData = userData;
  }

  setUnreads(unreads: SupportedHome['unreads']) {
    if (!this.home) {
      return;
    }

    this.home.unreads = unreads;
  }

  setBalance(balance: Balance) {
    if (!this.home?.me) {
      return;
    }

    this.home.me.balance = balance;
  }

  isCurrentUser({nickname}: Pick<User, 'nickname'>) {
    return this.userData?.nickname === nickname;
  }

  /**
   * @deprecated move to dedicated entity for each home item
   */
  setHomeData(home: Home) {
    this.home = home;
  }

  setTotalReactions(count: number) {
    if (!this.home) {
      return;
    }

    this.home.reactions.total_count = count;
  }

  setMe(me: Me) {
    if (!this.home) {
      return;
    }

    this.home.me = me;
    this.userData = me.user;
  }

  setSubscriptionData(subscription: Subscription) {
    if (!this.home) {
      return;
    }

    this.home.me.subscription = subscription;
  }

  isPassedOnboarding(name: Onboardings) {
    if (!Array.isArray(this.home?.onboardings)) {
      return false;
    }

    return this.home.onboardings.includes(name);
  }

  passedOnboarding(name: Onboardings) {
    if (!Array.isArray(this.home?.onboardings)) {
      return false;
    }

    this.home.onboardings.push(name);
  }

  setVerified(value: boolean) {
    if (!this.userData) {
      return;
    }
    this.userData.verified = value;
  }

  setLocationVerified(now = Date.now()) {
    if (!this.userData) {
      return;
    }
    this.userData.location_verified_at = now;
  }
  getAvatars() {
    if (!this.userData || !this.home) {
      return [];
    }

    return this.userData.avatars.map((id, index) => ({
      id,
      url: this.getAvatarAccessUrl(this.userData?.avatar_urls?.[index]),
    }));
  }

  getAvatarAccessUrl(url?: string) {
    if (!this.userData || !this.home || !url) {
      return '';
    }

    return `${url}?${this.home.pub_urls_query}`;
  }

  getUserBestAvatarUrl(
    user: {avatar_urls?: string[]; avatar_url?: string} | undefined
  ) {
    if (!this.home || !user) {
      return '';
    }

    if (user.avatar_url) {
      return this.getAvatarAccessUrl(user.avatar_url);
    }

    return this.getAvatarAccessUrl(user.avatar_urls?.[0]);
  }

  getRestAvatarUrls(user: User) {
    if (!this.home || !Array.isArray(user.avatar_urls)) {
      return [];
    }

    return user.avatar_urls.slice(1).map(this.getAvatarAccessUrl.bind(this));
  }

  get profileProgress(): number {
    const user = this?.userData;

    if (!user) {
      return 0;
    }

    let progress = 0;
    if (user.avatars) {
      progress = userAvatarsLength(user) * 3.125; //25
    }
    if (user.about && user.about.length > 0) {
      progress += 5; //30
    }
    if (user.school) {
      progress += 5; //35
    }
    if (user.school_info) {
      progress += 5; //40
    }
    if (user.work) {
      progress += 5; //45
    }
    if (user.work_info) {
      progress += 5; //50
    }
    if (user.interests && user.interests.length > 0) {
      progress += 5; //55
    }
    if (user.languages) {
      progress += 5; //60
    }
    if (user.verified) {
      progress += 7.5; //67.5
    }
    if (user.height_sm !== 0) {
      progress += 2.5; //70
    }
    if (user.weight_kg !== 0) {
      progress += 2.5; //72.5
    }
    if (user.sex_orientation !== 0) {
      progress += 2.5; //75
    }
    if (user.star_sign !== 0) {
      progress += 2.5; //77.5
    }
    if (user.exercise !== 0) {
      progress += 2.5; //80
    }
    if (user.education_level !== 0) {
      progress += 2.5; //82.5
    }
    if (user.marital_status !== 0) {
      progress += 2.5; //85
    }
    if (user.kids !== 0) {
      progress += 2.5; //87.5
    }
    if (user.drinking !== 0) {
      progress += 2.5; //90
    }
    if (user.smoking !== 0) {
      progress += 2.5; //92.5
    }
    if (user.pets !== 0) {
      progress += 2.5; //95
    }
    if (user.religion !== 0) {
      progress += 2.5; //97.5
    }
    if (user.values !== 0) {
      progress += 2.5; //100
    }
    if (progress > 100) {
      progress = 100;
    }
    return Math.round(progress);
  }
}

export default new UserStore();
